import { useEffect, useState } from "react";
import {
  Play, Square, Plus, Clock, Trash2, Paperclip, Flag, Loader2, Eye,
} from "lucide-react";
import {
  useActivityTaskStore,
  type ActivityStatus,
  type ActivityTask,
} from "../store/useActivityTaskStore";
import StartActivityModal from "../components/activities/StartActivityModal";
import ActivityDetailsModal from "../components/activities/ActivityDetailsModal";
import { fmtDuration, displayElapsed } from "../lib/activityTime";
import { useAuthStore } from "../store/useAuthStore";

const STATUSES: ActivityStatus[] = ["Planned", "In Progress", "Completed", "On Hold"];

const priorityClass = (p: string) => {
  if (p === "High") return "text-rose-600";
  if (p === "Medium") return "text-amber-600";
  return "text-slate-500";
};

export default function ActivitiesPage() {
  const {
    activities, activitiesUpdatedAt, pending, loading, fetchActivities, syncActivities,
    startActivity, stopActivity, changeStatus, deleteActivity,
  } = useActivityTaskStore();

  const { authUser } = useAuthStore();
  // BDs get the workspace chrome (own top bar); admins render under the fixed
  // global Navbar (h-16), so their pages need a top offset to clear it.
  const isBD = authUser?.role === "Business Developer";

  const [modalOpen, setModalOpen] = useState(false);
  const [viewActivity, setViewActivity] = useState<ActivityTask | null>(null);
  const [filter, setFilter] = useState<ActivityStatus | "All">("All");
  const [, setTick] = useState(0); // forces re-render for live timers

  useEffect(() => {
    fetchActivities();
    // Reconcile with ERP on load + every 7s (two-way sync).
    syncActivities();
    const syncId = setInterval(() => syncActivities(), 3000);
    return () => clearInterval(syncId);
  }, []);

  // Tick every second so running timers update.
  useEffect(() => {
    const anyRunning = activities.some((a) => a.runningSince);
    if (!anyRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [activities]);

  const shown =
    filter === "All" ? activities : activities.filter((a) => a.status === filter);

  return (
    <div className={`bd-workspace p-6 lg:p-8 max-w-[1400px] mx-auto w-full ${isBD ? "" : "pt-20 lg:pt-20"}`}>
      {/* Header */}
      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-6 flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[26px] font-bold text-on-surface tracking-tight">Activities</h1>
          <p className="text-[14px] text-on-surface-variant mt-1">All your tasks and their timers.</p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-[#004b9e] transition-colors text-sm font-semibold shrink-0"
        >
          <Plus size={18} /> <span className="hidden sm:inline">Start Activity</span>
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-sm mb-md flex-wrap">
        {(["All", ...STATUSES] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-md py-xs rounded-full font-label-md text-label-md transition-colors ${
              filter === s
                ? "bg-primary text-on-primary"
                : "bg-surface-container text-on-surface-variant hover:bg-surface-container-highest"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
          <Loader2 size={18} className="animate-spin text-[#2462b2]" /> Loading activities...
        </div>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl text-slate-400">
          No activities yet. Click <b>Start Activity</b> to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((a) => {
            const cat = typeof a.category === "object" ? a.category : null;
            const elapsed = displayElapsed(a, activitiesUpdatedAt);
            const isRunning = Boolean(a.runningSince);
            const isCompleted = a.status === "Completed";
            return (
              <div
                key={a._id}
                className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4"
              >
                {/* Left: info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {cat && (
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
                        style={{ background: cat.color || "#2462b2" }}
                      >
                        {cat.name}
                      </span>
                    )}
                    <span className={`text-[11px] font-semibold flex items-center gap-1 ${priorityClass(a.priority)}`}>
                      <Flag size={11} /> {a.priority}
                    </span>
                    {a.attachments?.length > 0 && (
                      <span className="text-[11px] text-slate-400 flex items-center gap-1">
                        <Paperclip size={11} /> {a.attachments.length}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-800 mt-1 truncate">
                    {a.title || a.description || "Untitled activity"}
                  </h3>
                  {(a.aiEnhancedDescription || a.description) && (
                    <p className="text-sm text-slate-500 line-clamp-1">
                      {a.aiEnhancedDescription || a.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                    {!isBD && (a as any).createdBy?.fullName && (
                      <span className="font-semibold text-primary">BD: {(a as any).createdBy.fullName}</span>
                    )}
                    {a.relatedLead && <span>Lead: {a.relatedLead.name}</span>}
                    {a.relatedJob && <span>Job: {a.relatedJob.jobTitle}</span>}
                    {a.estimatedMinutes > 0 && <span>Est: {fmtDuration(a.estimatedMinutes * 60)}</span>}
                  </div>
                </div>

                {/* Middle: timer */}
                <div className="flex items-center gap-2 font-mono text-lg font-bold text-slate-700">
                  <Clock size={16} className={isRunning ? "text-amber-500 animate-pulse" : "text-slate-400"} />
                  {fmtDuration(elapsed)}
                </div>

                {/* Right: controls */}
                <div className="flex items-center gap-2">
                  <select
                    className="px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2]"
                    value={a.status}
                    onChange={(e) => changeStatus(a._id, e.target.value as ActivityStatus)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>

                  {!isCompleted && (
                    isRunning ? (
                      <button
                        onClick={() => stopActivity(a._id)}
                        disabled={pending[a._id]}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {pending[a._id] ? <Loader2 size={14} className="animate-spin" /> : <Square size={14} />} Stop
                      </button>
                    ) : (
                      <button
                        onClick={() => startActivity(a._id)}
                        disabled={pending[a._id]}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#2462b2] hover:bg-[#1e52a0] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {pending[a._id] ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} fill="currentColor" />} Start
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setViewActivity(a)}
                    title="View details"
                    className="p-1.5 text-slate-400 hover:text-[#2462b2] hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Eye size={15} />
                  </button>

                  <button
                    onClick={() => {
                      if (confirm("Delete this activity?")) deleteActivity(a._id);
                    }}
                    className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <StartActivityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => fetchActivities()}
      />

      {viewActivity && (
        <ActivityDetailsModal activity={viewActivity} onClose={() => setViewActivity(null)} />
      )}
    </div>
  );
}
